/**
 * The signed-in customer's orders: each pouch and the five statuses they see (ACC-020, FUL-030).
 * Pharmacy internals never appear.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { CustomerOrder, CustomerOrderStatus } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { SUPPORT_ADDRESS } from "../PublicPage.tsx";
import { loadAccountOrders } from "../api.ts";
import { AccountShell } from "./Shell.tsx";
const DAY = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC"
});
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "signed_out";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "ready";
  readonly orders: readonly CustomerOrder[];
};
function day(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : DAY.format(date);
}
function statusLabel(status: CustomerOrderStatus): string {
  switch (status) {
    case "received":
      return "Order received";
    case "preparing":
      return "Preparing your formula";
    case "shipped":
      return "Shipped";
    case "delivered":
      return "Delivered";
    case "attention":
      return "Action needed";
  }
}
function statusState(status: CustomerOrderStatus): "pending" | "ok" | "problem" {
  switch (status) {
    case "delivered":
    case "shipped":
      return "ok";
    case "attention":
      return "problem";
    case "received":
    case "preparing":
      return "pending";
  }
}
function Tracking({
  order
}: {
  readonly order: CustomerOrder;
}) {
  if (order.status !== "shipped" && order.status !== "delivered") return null;
  if (order.trackingUrl !== null) return <a href={order.trackingUrl} rel="noreferrer" target="_blank" className="x3nfvp2 x6s0dn4 x1gg8mnh x10dbofl xqu410z x1hl2dhg">
        Track your pouch
      </a>;
  if (order.trackingNumber !== null) return <p className="x1ghz6dp x1l1vl4p">Tracking {order.trackingNumber}</p>;
  return null;
}
function OrderRow({
  order
}: {
  readonly order: CustomerOrder;
}) {
  return <li data-testid="order" className="x78zum5 xdt5ytf x1173whf xfjredr xez8t42 x13fuv20 x178xt8z">
      <p className="x1ghz6dp xl4dl4e x1jty25b x1l1vl4p">Placed {day(order.placedAt)}</p>
      <Status state={statusState(order.status)}>{statusLabel(order.status)}</Status>
      {order.status === "attention" ? <p className="x1ghz6dp x1jty25b x1fzhlzt">
          Something changed with this order. Write to {SUPPORT_ADDRESS} and we'll tell you what
          happened and what comes next.
        </p> : null}
      <Tracking order={order} />
    </li>;
}
function OrderList({
  orders
}: {
  readonly orders: readonly CustomerOrder[];
}) {
  if (orders.length === 0) {
    return <p className="x1ghz6dp x1jty25b x1fzhlzt">
        No pouches yet. The first one shows up here once you place an order.
      </p>;
  }
  return <ol data-testid="orders" className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf">
      {orders.map(order => <OrderRow key={order.id} order={order} />)}
    </ol>;
}
export function AccountOrders() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const load = () => {
    setScreen({
      kind: "loading"
    });
    void loadAccountOrders().then(loaded => {
      if (loaded === "signed_out") setScreen({
        kind: "signed_out"
      });else if (loaded === null) setScreen({
        kind: "failed"
      });else setScreen({
        kind: "ready",
        orders: loaded.orders
      });
    }, () => setScreen({
      kind: "failed"
    }));
  };
  useEffect(() => {
    document.title = "Your orders";
    load();
  }, []);
  if (screen.kind === "loading") return <AccountShell title="Your orders">
        <Pending message="Loading your orders" bones={["title", "line", "row", "row"]} />
      </AccountShell>;
  if (screen.kind === "signed_out") return <AccountShell title="Your orders" signedOut gateTitle="Sign in to see your orders">
        {null}
      </AccountShell>;
  if (screen.kind === "failed") return <AccountShell title="We couldn't load your orders">
        <p className="x1ghz6dp x1jty25b x1fzhlzt">
          Try again in a minute. If it keeps happening, write to support.
        </p>
        <Button onClick={load}>Try again</Button>
      </AccountShell>;
  return <AccountShell title="Your orders">
      <p className="x1ghz6dp x1jty25b x1fzhlzt">Each pouch, and where it is.</p>
      <OrderList orders={screen.orders} />
    </AccountShell>;
}